import { getAllCookies, getCookie, removeCookie, setCookie } from "./cookies";
import { safeJSONParse, safeJSONStringify } from "./utils";

export interface IConfigStore {
  set: (_key: string, _value: unknown) => void;
  /**
   *
   * @param key
   * @param value
   * @param exporationDays number of days before the data expires.
   * @returns
   */
  setWithExpiration: (
    _key: string,
    _value: unknown,
    _expirationDays: number
  ) => void;
  get: (_key: string) => unknown | undefined;
  remove: (_key: string) => void;
  clear: () => void;
}

function getFullKey(namespace: string, key: string): string {
  return `${namespace}.${key}`;
}

export default class CookieConfigStore implements IConfigStore {
  private readonly inMemory = new Map<string, unknown>();
  constructor(
    readonly namespace: string,
    readonly domain?: string
  ) {}

  set(key: string, value: unknown): void {
    const fullKey = getFullKey(this.namespace, key);
    this.inMemory.set(fullKey, value);
    this.setWithExpiration(key, value, 365);
  }

  setWithExpiration(key: string, value: unknown, exporationDays: number): void {
    const fullKey = getFullKey(this.namespace, key);
    this.inMemory.set(fullKey, value);
    const strVal = safeJSONStringify(value);
    if (strVal != null)
      setCookie(fullKey, strVal, {
        expires: exporationDays,
        domain: this.domain,
      });
  }

  get(key: string): unknown {
    const fullKey = getFullKey(this.namespace, key);
    const strValue = getCookie(fullKey);
    if (strValue != null) {
      const parsedValue = safeJSONParse(strValue);
      this.inMemory.set(fullKey, parsedValue);
      return parsedValue;
    }
    if (this.inMemory.has(fullKey)) {
      return this.inMemory.get(fullKey);
    }
    return undefined;
  }

  remove(key: string): void {
    const fullKey = getFullKey(this.namespace, key);
    removeCookie(fullKey, {
      domain: this.domain,
    });
    this.inMemory.delete(fullKey);
  }

  clear(): void {
    const prefix = `${this.namespace}.`;
    Object.keys(getAllCookies()).forEach((key) => {
      if (!key.startsWith(prefix)) return;

      // Clear both host-only and cross-subdomain variants. A customer may
      // change acrossDomain between visits, leaving either form behind.
      removeCookie(key);
      if (this.domain != null) {
        removeCookie(key, { domain: this.domain });
      }
    });
    this.inMemory.clear();
  }
}
